import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { usePode } from "@/lib/permissoes";
import { useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Copy, LayoutDashboard, Mail, Settings, Sparkles, Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { numeroBR, formatarDuracao } from "@shared/irrigacao";
import { dataCurtaBR, diaSemanaAbrev, somarDias } from "@shared/boletim-periodo";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import {
  useAceitarSugestoesMapeamento, useConfiguracaoIrrigacao, useDashboardIrrigacao, useEnviarRelatorio,
  useRegistrosIrrigacao, useSalvarConectorIrrigacao, useSalvarSetoresDaEstacao, useSalvarVazao,
  type DiaDeIrrigacao, type EstacaoIrrigacao, type RegistroSetor, type ResultadoIngestao,
} from "@/hooks/use-irrigacao";
import { DashboardIrrigacaoPainel } from "@/components/manejo/dashboard-irrigacao";
import { GradeDeMetas } from "@/components/manejo/grade-metas";

import { PageHeader } from "@/components/layout/page-header";
import { PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum, LinhasVazias,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import { PageTabs } from "@/components/layout/page-tabs";
// Ciclo 1 = INGESTÃO. Dashboard e metas são o ciclo 2, e a guia fica aqui vazia de
// propósito: o lugar dela já existe, e ninguém precisa procurar depois onde ela vai.

const PERIODOS = [
  { rotulo: "Ontem", dias: 1 },
  { rotulo: "7 dias", dias: 7 },
  { rotulo: "30 dias", dias: 30 },
  { rotulo: "90 dias", dias: 90 },
];

function ChipStatus({ status }: { status: DiaDeIrrigacao["status"] }) {
  if (status === "ok") return <StatusBadge tom="sucesso" ponto>ok</StatusBadge>;
  if (status === "divergente") return <StatusBadge tom="atencao" ponto>divergente</StatusBadge>;
  return <StatusBadge tom="erro" ponto>recusado</StatusBadge>;
}

/**
 * A MESMA declaração de colunas nos grupos de dia — e é por isso que ela é uma
 * constante de módulo, não um literal repetido dentro do `DataCard`.
 *
 * Cada dia é uma tabela independente. Com `width` no `<th>` cada uma se
 * auto-dimensiona pelo próprio conteúdo, e dois dias vizinhos dentro do mesmo
 * card ficam com "Volume" em posições diferentes — foi exatamente o defeito
 * desta tela. Com o `<colgroup>` idêntico, elas alinham.
 */
const COLUNAS_DIA = [null, "6.5rem", "8rem", "7rem", "8rem", "7rem"];

function LinhaRegistro({ registro }: { registro: RegistroSetor }) {
  return (
    <DataTableRow>
      {/* O setor é a coluna que responde à pergunta da tela. */}
      <DataTableCell destaque>
        {registro.setorNome}
        {registro.divideEstacaoCom && (
          // Setor que divide estação nunca terá lâmina individual real — dizer, não esconder.
          <span className="ml-1.5 text-xs text-muted-foreground">est. {registro.divideEstacaoCom}</span>
        )}
        {registro.estacoesEmParalelo && (
          // O tempo aqui é o da estação mais longa, não a soma das duas. Sem o rótulo,
          // volume de duas estações com tempo de uma parece número errado.
          <span className="ml-1.5 text-xs text-muted-foreground">
            {registro.estacoesEmParalelo} em paralelo
          </span>
        )}
      </DataTableCell>
      <DataTableNum>{formatarDuracao(registro.runtimeS)}</DataTableNum>
      <DataTableNum>
        {registro.vazaoMediaM3h !== null ? `${numeroBR(registro.vazaoMediaM3h, 1)} m³/h` : "—"}
      </DataTableNum>
      <DataTableNum>{numeroBR(registro.volumeM3, 1)} m³</DataTableNum>
      <DataTableNum className="font-semibold">
        {registro.leitura !== null ? `${numeroBR(registro.leitura, 2)} ${registro.unidadeRotulo}` : "—"}
      </DataTableNum>
      <DataTableNum className="text-muted-foreground">
        {registro.leituraPorHora !== null ? numeroBR(registro.leituraPorHora, 2) : "—"}
      </DataTableNum>
    </DataTableRow>
  );
}

function GrupoDoDia({ dia }: { dia: DiaDeIrrigacao }) {
  return (
    <DataCard
      titulo={`${diaSemanaAbrev(dia.data)} ${dataCurtaBR(dia.data)}`}
      meta={
        <>
          <span className="tabular-nums">
            {dia.volumeMedidoM3 !== null
              ? `${numeroBR(dia.volumeMedidoM3, 1)} m³ medidos`
              : `${numeroBR(dia.volumeEstimadoTotalM3, 1)} m³ estimados`}
          </span>
          {" · "}
          <span>{dia.setoresComRegistro} setores</span>
          {dia.revisao > 1 && <> · revisão {dia.revisao}</>}
        </>
      }
      estado={
        <div className="flex items-center gap-1.5">
          <ChipStatus status={dia.status} />
          {dia.origem === "email" && <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-label="chegou por e-mail" />}
        </div>
      }
      /* Consequência, nunca diagnóstico — o motivo já vem escrito assim do servidor.
         Era uma linha em `text-atencao` escrita à mão; virou o slot `aviso`. */
      aviso={dia.motivo}
      testId={`dia-${dia.data}`}
    >
      <DataTable colunas={COLUNAS_DIA}>
        <DataTableHeader>
          <DataTableRow>
            <DataTableHead>Setor</DataTableHead>
            <DataTableHead num>Tempo</DataTableHead>
            <DataTableHead num>Vazão média</DataTableHead>
            <DataTableHead num>Volume</DataTableHead>
            <DataTableHead num>Lâmina</DataTableHead>
            <DataTableHead num>Lâmina/h</DataTableHead>
          </DataTableRow>
        </DataTableHeader>
        <DataTableBody>
          {dia.registros.map(r => <LinhaRegistro key={r.setorId} registro={r} />)}
          {/* Setor que não irrigou NÃO vira linha de "0,0 m³" — isso afirmaria
              medição onde não houve nenhuma. Colapsa no expansor. */}
          <LinhasVazias
            quantidade={dia.semIrrigacao.length}
            rotulo="setores sem irrigação"
            amostra={dia.semIrrigacao}
            colSpan={6}
          >
            <DataTableRow className="hover:bg-transparent">
              <DataTableCell colSpan={6} className="text-xs text-muted-foreground">
                {dia.semIrrigacao.join(" · ")}
              </DataTableCell>
            </DataTableRow>
          </LinhasVazias>
        </DataTableBody>
      </DataTable>
    </DataCard>
  );
}

function ResultadoDoUpload({ resultado }: { resultado: ResultadoIngestao }) {
  const cor = resultado.status === "ok" ? "border-sucesso/30 bg-sucesso-suave text-sucesso"
    : resultado.status === "rejeitada" ? "border-erro/30 bg-erro-suave text-erro"
    : "border-atencao/30 bg-atencao-suave text-atencao";
  const titulo = resultado.status === "ignorada"
    ? "Esse arquivo já tinha entrado — nada mudou."
    : resultado.status === "rejeitada"
      ? "O relatório não pôde virar registro."
      : `${resultado.dataReferencia} · ${resultado.setores} setores, ${resultado.estacoes} estações.`;
  return (
    <div className={`rounded-md border px-3 py-2 text-xs ${cor}`}>
      <p className="font-medium">{titulo}</p>
      {resultado.estacoesDescobertas > 0 && (
        <p className="mt-1">
          {resultado.estacoesDescobertas} estações e {resultado.vazoesSemeadas} vazões foram descobertas do próprio
          relatório. Confira as vazões em ⚙ Configurações — enquanto não forem confirmadas, a validação de vazão
          fica desligada nelas.
        </p>
      )}
      {resultado.motivos.map((m, i) => <p key={i} className="mt-1">{m}</p>)}
    </div>
  );
}

// Os dois diálogos recebem a estação por PROP e são montados com `key` — o estado
// inicial sai do props na montagem, sem useEffect de sincronização. Sem a key, abrir a
// segunda estação reaproveitaria o valor digitado na primeira.
function DialogVazao({ estacao, aoFechar }: { estacao: EstacaoIrrigacao; aoFechar: () => void }) {
  const { toast } = useToast();
  const salvar = useSalvarVazao();
  const [valor, setValor] = useState(estacao.vazaoM3h !== null ? String(estacao.vazaoM3h).replace(".", ",") : "");

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) aoFechar(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vazão da estação {estacao.numero} · {estacao.nome}</DialogTitle>
          <DialogDescription>
            É este valor que converte tempo de rega em volume. Salvar aqui fecha a vigência do valor anterior e
            registra o novo — o histórico não se reescreve.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="vazao">Vazão (m³/h)</Label>
          <Input id="vazao" inputMode="decimal" value={valor} onChange={e => setValor(e.target.value)} placeholder="81,06" />
          {!estacao.vazaoConfirmada && estacao.vazaoM3h !== null && (
            <p className="text-xs text-atencao">
              O valor atual veio do próprio relatório e ainda não foi confirmado. Confirmar liga a validação de
              vazão desta estação.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>Cancelar</Button>
          <Button
            disabled={salvar.isPending || valor.trim() === ""}
            onClick={async () => {
              try {
                await salvar.mutateAsync({ estacaoId: estacao.id, vazaoM3h: valor });
                toast({ title: "Vazão atualizada" });
                aoFechar();
              } catch (e: any) {
                toast({ title: "Não foi possível salvar", description: e.message, variant: "destructive" });
              }
            }}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogSetores({ estacao, setores, aoFechar }: {
  estacao: EstacaoIrrigacao;
  setores: Array<{ id: string; nome: string }>;
  aoFechar: () => void;
}) {
  const { toast } = useToast();
  const salvar = useSalvarSetoresDaEstacao();
  // Estação ainda sem vínculo abre com a SUGESTÃO já marcada — mas é o Salvar que
  // cria o vínculo. Sugestão que se aplica sozinha jogaria a água de um setor no outro.
  const [selecionados, setSelecionados] = useState<string[]>(
    estacao.setorIds.length > 0 ? estacao.setorIds : estacao.setorIdsSugeridos,
  );
  const [simultanea, setSimultanea] = useState(estacao.simultanea);

  const alternar = (id: string) => {
    setSelecionados(atual => atual.indexOf(id) === -1 ? atual.concat([id]) : atual.filter(x => x !== id));
  };

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) aoFechar(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Setores da estação {estacao.numero} · {estacao.nome}</DialogTitle>
          <DialogDescription>
            Uma estação pode cobrir mais de um setor — o volume é rateado pela área deles, e a lâmina sai igual
            nos dois. {estacao.setorIds.length === 0 && estacao.setorIdsSugeridos.length > 0
              ? "Os marcados são sugestão por semelhança de nome; confirme antes de salvar."
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {setores.map(s => (
            <label key={s.id} className="flex items-center gap-2 text-sm">
              <Checkbox checked={selecionados.indexOf(s.id) !== -1} onCheckedChange={() => alternar(s.id)} />
              {s.nome}
            </label>
          ))}
        </div>

        {/* O relatório traz DURAÇÕES, não janelas — não há como o sistema descobrir
            sozinho que duas estações abrem juntas. Tem que vir daqui. */}
        <div className="rounded-md border p-3">
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={simultanea}
              onCheckedChange={valor => setSimultanea(valor === true)}
            />
            <span>
              Abre ao mesmo tempo que as outras estações destes setores
              <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                Só faz sentido quando o setor é regado por mais de uma estação. Sem isto, o
                tempo das duas é somado e o mesmo minuto de relógio conta duas vezes — a
                vazão média e a lâmina/h saem pela metade.
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>Cancelar</Button>
          <Button
            disabled={salvar.isPending}
            onClick={async () => {
              try {
                await salvar.mutateAsync({ estacaoId: estacao.id, setorIds: selecionados, simultanea });
                toast({ title: "Mapeamento salvo" });
                aoFechar();
              } catch (e: any) {
                toast({ title: "Não foi possível salvar", description: e.message, variant: "destructive" });
              }
            }}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ABAS = [
  { id: "dashboard", label: "Dashboard", icone: LayoutDashboard },
  { id: "registros", label: "Registros" },
  { id: "config", label: "Configurações", icone: Settings },
];

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook). `dias`/`cultura`/
// `de`/`ate` seguem locais: são o recorte da INGESTÃO do PDF, um fluxo de
// importação, não o recorte da lista.
const PADRAO_IRRIGACAO = { aba: "registros", q: "" };
const PERMITIDOS_IRRIGACAO = { aba: ["registros", "config"] } as const;

export default function ManejoIrrigacao() {
  const { toast } = useToast();
  const { estado, definir } = useEstadoNaUrl(PADRAO_IRRIGACAO, PERMITIDOS_IRRIGACAO);
  // A aba Configurações (vazão, setores, conector, metas) é
  // `manejo.irrigacao.configurar` (ADMIN/GESTOR): sem ela, a aba some e um
  // `?aba=config` de link velho cai no Dashboard.
  const podeConfigurar = usePode("manejo.irrigacao.configurar");
  const abas = podeConfigurar ? ABAS : ABAS.filter(a => a.id !== "config");
  const aba = estado.aba === "config" && !podeConfigurar ? "dashboard" : estado.aba;
  const setAba = (v: string) => definir({ aba: v });
  // Filtro NO CLIENTE, sobre os registros já carregados — não entra em queryKey,
  // mas entra na URL: reload e "voltar" caem na mesma aba, com a mesma busca.
  const busca = estado.q;
  const [dias, setDias] = useState(30);
  const [cultura, setCultura] = useState<string | null>(null);
  // De–Até explícito: quando o usuário digita uma ponta, os chips deixam de mandar.
  const [deLivre, setDeLivre] = useState<string | null>(null);
  const [ateLivre, setAteLivre] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoIngestao | null>(null);
  const [vazaoDe, setVazaoDe] = useState<EstacaoIrrigacao | null>(null);
  const [setoresDe, setSetoresDe] = useState<EstacaoIrrigacao | null>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  const hoje = hojeNaFazenda();
  // O relatório do dia chega na manhã seguinte, então o chip "Ontem" aponta para ontem —
  // hoje quase nunca tem leitura, e abrir a tela num dia vazio parece defeito.
  const ate = ateLivre ?? (dias === 1 ? somarDias(hoje, -1) : hoje);
  const de = deLivre ?? somarDias(ate, -(dias - 1));
  const periodoLivre = deLivre !== null || ateLivre !== null;

  const registros = useRegistrosIrrigacao({ de, ate });
  const config = useConfiguracaoIrrigacao();
  const dashboard = useDashboardIrrigacao({ de, ate, cultura });
  const enviar = useEnviarRelatorio();

  // A lista do dashboard vem dos SETORES (completa). A derivada dos registros é reserva
  // para quando o dashboard ainda não respondeu — os chips não podem sumir e voltar.
  const culturas = useMemo(() => {
    if ((dashboard.data?.culturas.length ?? 0) > 0) return dashboard.data!.culturas;
    const nomes: string[] = [];
    for (const dia of registros.data?.dias ?? []) {
      for (const r of dia.registros) {
        if (r.culturaNome && nomes.indexOf(r.culturaNome) === -1) nomes.push(r.culturaNome);
      }
    }
    return nomes;
  }, [registros.data, dashboard.data]);

  const diasFiltrados = useMemo(() => {
    const todos = registros.data?.dias ?? [];
    if (!cultura) return todos;
    return todos
      .map(d => ({ ...d, registros: d.registros.filter(r => r.culturaNome === cultura) }))
      .filter(d => d.registros.length > 0);
  }, [registros.data, cultura]);

  // A busca recorta os SETORES dentro de cada dia, no cliente. Um dia em que
  // nenhum setor casa some da lista — mostrar o cabeçalho do dia com a tabela
  // vazia faria parecer que aquele dia não teve irrigação.
  const diasVisiveis = useMemo(() => {
    if (!busca.trim()) return diasFiltrados;
    return diasFiltrados
      .map(d => ({ ...d, registros: d.registros.filter(r => combina(busca, r.setorNome)) }))
      .filter(d => d.registros.length > 0);
  }, [diasFiltrados, busca]);

  const aoEscolherArquivo = async (arquivo: File | null) => {
    if (!arquivo) return;
    setResultado(null);
    try {
      const r = await enviar.mutateAsync(arquivo);
      setResultado(r);
      if (r.status === "rejeitada") toast({ title: "Relatório recusado", variant: "destructive" });
      else if (r.status === "ignorada") toast({ title: "Esse arquivo já tinha entrado" });
      else toast({ title: `Relatório de ${r.dataReferencia} registrado` });
    } catch (e: any) {
      toast({ title: "Não foi possível enviar", description: e.message, variant: "destructive" });
    } finally {
      if (arquivoRef.current) arquivoRef.current.value = "";
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <PageHeader
        titulo="Irrigação"
        descricao="Quanta água cada setor recebeu, por dia — do relatório do controlador ao registro auditável."
      />

      {/* Sub-navegação: troca de PÁGINA dentro da página. O Radix Tabs continua
          sendo o comutador de conteúdo — só a TabsList some, virou PageTabs. */}
      <PageTabs abas={abas} ativa={aba} onChange={setAba} />

      <Tabs value={aba} onValueChange={setAba}>

        <TabsContent value="dashboard" className="space-y-3">
          <BarraDeFiltros
            dias={dias} setDias={d => { setDias(d); setDeLivre(null); setAteLivre(null); }}
            de={de} ate={ate} periodoLivre={periodoLivre}
            setDe={setDeLivre} setAte={setAteLivre}
            cultura={cultura} setCultura={setCultura}
            culturas={culturas}
          />
          <DashboardIrrigacaoPainel consulta={dashboard} />
        </TabsContent>

        {/* ── Registros ────────────────────────────────────────────────── */}
        <TabsContent value="registros" className="space-y-3">
          {/* A MESMA barra do Dashboard — um filtro só, valendo para as duas guias. */}
          <div className="flex flex-wrap items-start justify-between gap-2">
            <BarraDeFiltros
              dias={dias} setDias={d => { setDias(d); setDeLivre(null); setAteLivre(null); }}
              de={de} ate={ate} periodoLivre={periodoLivre}
              setDe={setDeLivre} setAte={setAteLivre}
              cultura={cultura} setCultura={setCultura}
              culturas={culturas}
            />
            <div className="flex items-center gap-2">
              <PageSearch
                valor={busca}
                onChange={v => definir({ q: v })}
                placeholder="Setor"
                testId="input-busca-irrigacao"
              />
              <input
                ref={arquivoRef} type="file" accept="application/pdf,.pdf" className="hidden"
                onChange={e => aoEscolherArquivo(e.target.files?.[0] ?? null)}
              />
              <Button variant="outline" className="shrink-0" disabled={enviar.isPending} onClick={() => arquivoRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1.5" />
                {enviar.isPending ? "Lendo o relatório…" : "Enviar relatório (PDF)"}
              </Button>
            </div>
          </div>

          {resultado && <ResultadoDoUpload resultado={resultado} />}

          {registros.isLoading ? (
            <PageSkeleton variante="tabela" linhas={6} />
          ) : registros.isError ? (
            <p className="py-16 text-center text-sm text-destructive">
              {(registros.error as any)?.message ?? "Erro ao carregar os registros."}
            </p>
          ) : diasFiltrados.length === 0 ? (
            <EmptyState
              icone={Upload}
              titulo="Nenhum relatório neste período"
              descricao="Envie um PDF acima, ou ligue o recebimento por e-mail em ⚙ Configurações — a data do registro sai do conteúdo do relatório, então encaminhar os antigos em lote também funciona."
              testId="empty-irrigacao"
            />
          ) : diasVisiveis.length === 0 ? (
            <NadaEncontrado termo={busca} />
          ) : (
            /* Um card por dia. Todos declaram o MESMO `COLUNAS_DIA`, e é isso que
               mantém "Volume" na mesma posição de um card para o outro. */
            <div className="space-y-3">
              {diasVisiveis.map(dia => <GrupoDoDia key={dia.leituraId} dia={dia} />)}
            </div>
          )}

          {/* Falha de entrega não é silenciosa: o documento recusado aparece, com o motivo. */}
          {(registros.data?.recusados.length ?? 0) > 0 && (
            <div className="rounded-md border border-erro/30 bg-erro-suave p-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-erro">
                <AlertTriangle className="h-4 w-4" /> Documentos recusados
              </p>
              <ul className="mt-1.5 space-y-1 text-xs text-erro">
                {registros.data!.recusados.map(r => (
                  <li key={r.leituraId}>
                    <span className="font-medium">{r.arquivoNome ?? "(sem nome)"}</span>
                    {" — "}{r.motivo}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            <b>Vazão média</b> = volume ÷ tempo do próprio setor. Onde uma estação cobre dois setores, o volume é
            rateado pela área deles. Dia sem relatório não vira zero nem média — ele simplesmente não aparece.
          </p>
        </TabsContent>

        {/* ── ⚙ Configurações ──────────────────────────────────────────── */}
        <TabsContent value="config" className="space-y-4">
          <FaixaSugestoes config={config} />
          {/* Metas acima de Vazões: é a grade que o usuário mais volta para mexer. */}
          <GradeDeMetas />
          <BlocoVazoes config={config} onEditarVazao={setVazaoDe} onEditarSetores={setSetoresDe} />
          <BlocoFonte config={config} />
          <BlocoCadastros />
        </TabsContent>
      </Tabs>

      {vazaoDe && <DialogVazao key={`vazao-${vazaoDe.id}`} estacao={vazaoDe} aoFechar={() => setVazaoDe(null)} />}
      {setoresDe && (
        <DialogSetores
          key={`setores-${setoresDe.id}`} estacao={setoresDe}
          setores={config.data?.setores ?? []} aoFechar={() => setSetoresDe(null)}
        />
      )}
    </div>
  );
}

/** Barra única de filtros — os chips, o De–Até explícito e a cultura. */
function BarraDeFiltros({ dias, setDias, de, ate, periodoLivre, setDe, setAte, cultura, setCultura, culturas }: {
  dias: number;
  setDias: (d: number) => void;
  de: string;
  ate: string;
  periodoLivre: boolean;
  setDe: (v: string | null) => void;
  setAte: (v: string | null) => void;
  cultura: string | null;
  setCultura: (c: string | null) => void;
  culturas: string[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {PERIODOS.map(p => (
        <Button
          key={p.dias} size="sm"
          variant={!periodoLivre && dias === p.dias ? "default" : "outline"}
          onClick={() => setDias(p.dias)}
        >
          {p.rotulo}
        </Button>
      ))}
      <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
      {/* `text-xs` e largura elástica: no iPhone o `input[type=date]` escreve a data
          por extenso ("11 de ago. de 2026"), e em 16px num box de 9,5rem o texto
          vazava a borda. As duas datas dividem a linha, com o "até" no meio. */}
      <Input
        type="date" value={de} onChange={e => setDe(e.target.value || null)}
        className="h-8 flex-[1_1_8rem] min-w-0 text-xs sm:flex-none sm:w-[10rem]" aria-label="Data inicial"
      />
      <span className="text-sm text-muted-foreground">até</span>
      <Input
        type="date" value={ate} onChange={e => setAte(e.target.value || null)}
        className="h-8 flex-[1_1_8rem] min-w-0 text-xs sm:flex-none sm:w-[10rem]" aria-label="Data final"
      />
      {culturas.length > 1 && (
        <>
          <span className="mx-1 h-5 w-px bg-border" />
          <Button size="sm" variant={cultura === null ? "default" : "outline"} onClick={() => setCultura(null)}>
            Todas as culturas
          </Button>
          {culturas.map(c => (
            <Button key={c} size="sm" variant={cultura === c ? "default" : "outline"} onClick={() => setCultura(c)}>{c}</Button>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * O atalho que faltava no ciclo 1.
 *
 * Sem ele, ligar uma fazenda de 14 estações exigia abrir 14 diálogos e salvar um a um —
 * e o resultado no preview foi 14 estações descobertas, 14 vazões semeadas e ZERO
 * vínculos, com o dia entrando `divergente` e 0 registros por setor.
 */
function FaixaSugestoes({ config }: { config: ReturnType<typeof useConfiguracaoIrrigacao> }) {
  const { toast } = useToast();
  const aceitar = useAceitarSugestoesMapeamento();
  const pendentes = config.data?.sugestoesPendentes ?? 0;
  const semSugestao = config.data?.semSugestao ?? 0;
  const total = config.data?.estacoes.length ?? 0;
  if (pendentes === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-info/30 bg-info-suave px-4 py-3">
      <Sparkles className="h-4 w-4 shrink-0 text-info" />
      <p className="flex-1 text-sm text-info">
        <b>{pendentes} de {total} estações</b> têm um setor de mesmo nome e ainda não estão vinculadas.
        {semSugestao > 0 && ` ${semSugestao} sem sugestão continuam precisando da escolha manual.`}
      </p>
      <Button
        size="sm" disabled={aceitar.isPending}
        onClick={async () => {
          try {
            const r = await aceitar.mutateAsync();
            toast({
              title: `${r.aplicadas} estações vinculadas`,
              description: r.semSugestao > 0
                ? `${r.semSugestao} ainda precisam da escolha manual.`
                : "Suba o relatório de novo para os setores ganharem registro.",
            });
          } catch (e: any) {
            toast({ title: "Não foi possível aplicar", description: e.message, variant: "destructive" });
          }
        }}
      >
        {aceitar.isPending ? "Vinculando…" : "Aceitar todas as sugestões"}
      </Button>
    </div>
  );
}

function BlocoVazoes({ config, onEditarVazao, onEditarSetores }: {
  config: ReturnType<typeof useConfiguracaoIrrigacao>;
  onEditarVazao: (e: EstacaoIrrigacao) => void;
  onEditarSetores: (e: EstacaoIrrigacao) => void;
}) {
  const nomeDosSetores = (estacao: EstacaoIrrigacao): string => {
    const todos = config.data?.setores ?? [];
    const nomes = estacao.setorIds
      .map(id => todos.filter(s => s.id === id)[0]?.nome)
      .filter(n => !!n) as string[];
    return nomes.length > 0 ? nomes.join(" + ") : "—";
  };

  return (
    <DataCard
      titulo="Vazões de referência"
      meta="Uma por estação — o valor cadastrado é o que converte tempo de rega em volume."
      rodape={
        <>
          Relatório cujo volume implique vazão fora de <b>±30% do cadastrado</b> marca o dia como <b>divergente</b> —
          o valor do relatório nunca substitui o cadastro sozinho. Vazão marcada <b>a confirmar</b> veio do próprio
          relatório: até alguém confirmá-la, essa validação fica desligada naquela estação.
          Mudança física no setor (emissor, linha de gotejo, reforma) muda a vazão real — atualizar aqui.
        </>
      }
    >
      {config.isLoading ? (
        <PageSkeleton variante="tabela" linhas={4} className="border-0 rounded-none" />
      ) : config.isError ? (
        <p className="p-6 text-center text-sm text-destructive">Erro ao carregar as estações.</p>
      ) : (config.data?.estacoes.length ?? 0) === 0 ? (
        <EmptyState
          titulo="Nenhuma estação ainda"
          descricao="Envie o primeiro relatório na guia Registros — as estações e as vazões saem dele."
        />
      ) : (
        <div className="overflow-x-auto">
          <DataTable colunas={[null, "14rem", "10rem", "9rem", "6rem"]}>
            <DataTableHeader>
              <DataTableRow>
                <DataTableHead>Estação</DataTableHead>
                <DataTableHead>Setores</DataTableHead>
                <DataTableHead num>Vazão (m³/h)</DataTableHead>
                <DataTableHead>Atualizada em</DataTableHead>
                <DataTableHead />
              </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
              {config.data!.estacoes.map(e => (
                <DataTableRow key={e.id}>
                  <DataTableCell destaque>{e.numero} <span className="text-muted-foreground font-normal">{e.nome}</span></DataTableCell>
                  <DataTableCell>
                    <button className="underline underline-offset-2" onClick={() => onEditarSetores(e)}>
                      {nomeDosSetores(e)}
                    </button>
                    {e.setorIds.length === 0 && e.setorIdsSugeridos.length > 0 && (
                      <StatusBadge tom="atencao" className="ml-1.5">sugestão pronta</StatusBadge>
                    )}
                  </DataTableCell>
                  <DataTableNum>
                    <span className="inline-flex items-center justify-end gap-1.5">
                      {e.vazaoM3h !== null ? numeroBR(e.vazaoM3h, 2) : "—"}
                      {/* É este selo que explica por que a validação de ±30% está
                          desligada nesta estação. Badge, não texto solto: no ciclo 1
                          ele passava despercebido na tela. */}
                      {e.vazaoM3h !== null && !e.vazaoConfirmada && (
                        <StatusBadge tom="atencao">a confirmar</StatusBadge>
                      )}
                    </span>
                  </DataTableNum>
                  <DataTableCell className="text-muted-foreground">
                    {e.vazaoAtualizadaEm ? dataCurtaBR(e.vazaoAtualizadaEm) : "—"}
                  </DataTableCell>
                  <DataTableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => onEditarVazao(e)}>editar</Button>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        </div>
      )}
    </DataCard>
  );
}

function BlocoFonte({ config }: { config: ReturnType<typeof useConfiguracaoIrrigacao> }) {
  const { toast } = useToast();
  const salvar = useSalvarConectorIrrigacao();
  const [remetente, setRemetente] = useState<string | null>(null);

  const conector = config.data?.conector ?? null;
  const endereco = conector?.enderecoRecebimento ?? null;
  const valorRemetente = remetente !== null ? remetente : (conector?.remetenteEsperado ?? "");
  const ativo = !!conector?.recebimentoAtivo;

  const copiar = async () => {
    if (!endereco) return;
    try {
      await navigator.clipboard.writeText(endereco);
      toast({ title: "Endereço copiado" });
    } catch {
      // Sem permissão de área de transferência (http, navegador antigo): o endereço
      // continua visível e selecionável — não fingir que copiou.
      toast({ title: "Não foi possível copiar", description: "Selecione o endereço e copie à mão.", variant: "destructive" });
    }
  };

  return (
    <div className="rounded-md border">
      <div className="border-b px-4 py-3">
        <h2 className="font-semibold">Fonte de dados{conector ? ` — ${conector.rotulo}` : ""}</h2>
        <p className="text-sm text-muted-foreground">
          O relatório entra por upload (sempre) e, se você quiser, sozinho por e-mail: ponha o endereço abaixo
          entre os destinatários do relatório agendado do controlador.
        </p>
      </div>
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="endereco">Endereço de recebimento desta organização</Label>
          <div className="flex gap-2">
            <Input id="endereco" value={endereco ?? ""} readOnly className="font-mono text-xs" />
            <Button size="sm" variant="outline" onClick={copiar} disabled={!endereco}>
              <Copy className="h-4 w-4" />
              <span className="sr-only">Copiar endereço</span>
            </Button>
          </div>
          {/* Somente leitura de propósito: o endereço é gerado a partir da organização.
              Deixar o cliente digitá-lo foi o furo de multi-tenancy do ciclo 1. */}
          <p className="text-xs text-muted-foreground">
            Gerado pelo Rizoma e exclusivo desta organização — não é editável.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="remetente">Remetente esperado</Label>
          <Input
            id="remetente" value={valorRemetente} placeholder="no-reply@rainbird.com"
            onChange={e => setRemetente(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Em branco, aceita qualquer remetente que passe na assinatura.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={ativo}
            onCheckedChange={async valor => {
              try {
                await salvar.mutateAsync({ recebimentoAtivo: valor === true });
                toast({ title: valor ? "Recebimento por e-mail ligado" : "Recebimento por e-mail desligado" });
              } catch (e: any) {
                toast({ title: "Não foi possível salvar", description: e.message, variant: "destructive" });
              }
            }}
          />
          Receber relatório por e-mail neste endereço
        </label>
        <Button
          size="sm" disabled={salvar.isPending}
          onClick={async () => {
            try {
              // Só o remetente vai no corpo — o endereço é do servidor.
              await salvar.mutateAsync({ remetenteEsperado: valorRemetente.trim() === "" ? null : valorRemetente.trim() });
              setRemetente(null);
              toast({ title: "Fonte de dados salva" });
            } catch (e: any) {
              toast({ title: "Não foi possível salvar", description: e.message, variant: "destructive" });
            }
          }}
        >
          Salvar
        </Button>
      </div>
      {!ativo && (
        <p className="border-t px-4 py-2 text-xs text-atencao">
          Enquanto o recebimento estiver desligado, um e-mail enviado para esse endereço é ignorado.
        </p>
      )}
    </div>
  );
}

function BlocoCadastros() {
  return (
    <div className="rounded-md border">
      <div className="space-y-2 px-4 py-3 text-sm">
        <p>
          <span className="font-medium">Setores, área e plantas</span>{" — "}
          <Link href="/cacho/setores" className="underline underline-offset-2">Admin · Setores</Link>
          <span className="text-muted-foreground"> (a lâmina usa a área que já existe lá; não há campo novo)</span>
        </p>
        <p>
          <span className="font-medium">Culturas e unidades</span>{" — "}
          <Link href="/cacho/variedades" className="underline underline-offset-2">Admin · Culturas e Variedades</Link>
          <span className="text-muted-foreground"> (banana lê em mm; citros, em L/planta)</span>
        </p>
      </div>
      <p className="border-t px-4 py-3 text-xs text-muted-foreground">
        A Irrigação não recadastra nada que o sistema já tem — ela consome Setores e Culturas.
      </p>
    </div>
  );
}
